import { useMemo, useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import { RiCloseLine } from "react-icons/ri";
import Label from "../../Utility/Label/Label";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Input from "../../Utility/Input/Input";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import SingleDatePicker from "../../Utility/DatePickerRange/SingleDatePicker";
import { SAAS_MODULE_BILLING_TYPE_DROPDOWN_OPTIONS } from "./clientsDataUtils";

/** Allow only digits and at most one decimal point */
function toNumericOnly(value) {
  if (value === "" || value == null) return "";
  const s = String(value).replace(/[^\d.]/g, "");
  const parts = s.split(".");
  if (parts.length <= 1) return s;
  return parts[0] + "." + parts.slice(1).join("");
}

function seedSaasModuleRow(setValue, moduleId) {
  const key = String(moduleId);
  setValue(`saasModules.${key}.implementationFee`, "");
  setValue(`saasModules.${key}.monthlyFee`, "");
  setValue(`saasModules.${key}.additionalFee`, "");
  setValue(`saasModules.${key}.billingType`, "both");
  setValue(`saasModules.${key}.startDate`, "");
  setValue(`saasModules.${key}.endDate`, "");
  setValue(`saasModules.${key}.active`, true);
}

/** Catalog rows may use string names or nested API shapes — never render a raw object. */
function moduleDisplayLabel(mod) {
  if (mod == null) return "";
  const raw = mod.name ?? mod.label ?? mod.moduleName ?? mod.title ?? mod.displayName;
  if (raw == null || raw === "") return String(mod.id ?? "");
  if (typeof raw === "string" || typeof raw === "number") return String(raw);
  if (typeof raw === "object") {
    const nested = raw.label ?? raw.name ?? raw.en ?? raw.default ?? raw.value ?? raw.text;
    if (nested != null && (typeof nested === "string" || typeof nested === "number")) {
      return String(nested);
    }
  }
  return String(mod.id ?? "");
}

/**
 * SaaS client module grid: quick-add from catalog, only assigned modules shown.
 * Form paths: `saasModules.{moduleId}.*` (moduleId string).
 */
export default function ClientSaasModulesSection({
  control,
  watch,
  setValue,
  unregister,
  saasModuleCatalog,
  assignedModuleIds,
  onAssignedModuleIdsChange,
}) {
  /** Module ids picked in the multiselect before "Add modules" commits them to the grid. */
  const [modulesPendingAdd, setModulesPendingAdd] = useState([]);

  const catalogById = useMemo(() => {
    const m = new Map();
    (saasModuleCatalog ?? []).forEach((mod) => {
      if (mod?.id != null) m.set(String(mod.id), mod);
    });
    return m;
  }, [saasModuleCatalog]);

  const quickAddOptions = useMemo(() => {
    const assigned = new Set(assignedModuleIds);
    return (saasModuleCatalog ?? [])
      .filter((mod) => mod?.id != null && !assigned.has(String(mod.id)))
      .map((mod) => ({
        label: moduleDisplayLabel(mod),
        value: String(mod.id),
      }));
  }, [saasModuleCatalog, assignedModuleIds]);

  /** Drop pending picks that are no longer available (e.g. just added to the grid). */
  useEffect(() => {
    const allowed = new Set(quickAddOptions.map((o) => String(o.value)));
    setModulesPendingAdd((prev) => prev.filter((id) => allowed.has(String(id))));
  }, [quickAddOptions]);

  const handlePendingSelectionChange = (e) => {
    const v = e?.target?.value;
    setModulesPendingAdd(Array.isArray(v) ? v.map(String) : []);
  };

  const handleQuickAdd = () => {
    const assigned = new Set(assignedModuleIds.map(String));
    const toAdd = modulesPendingAdd
      .map(String)
      .filter((id) => !assigned.has(id) && catalogById.has(id));
    if (!toAdd.length) return;
    const next = [...assignedModuleIds.map(String)];
    for (const sid of toAdd) {
      if (!next.includes(sid)) {
        next.push(sid);
        seedSaasModuleRow(setValue, sid);
      }
    }
    onAssignedModuleIdsChange(next);
    setModulesPendingAdd([]);
  };

  const handleRemove = (moduleId) => {
    const sid = String(moduleId);
    onAssignedModuleIdsChange(assignedModuleIds.filter((id) => id !== sid));
    unregister(`saasModules.${sid}`);
  };

  if (!saasModuleCatalog?.length) return null;

  return (
    <div className="w-full mb-2 p-4 rounded-md border border-border-primary">
      <div className="flex flex-col gap-1 mb-3">
        <Typography variant="h5" className="text-text-primary font-semibold m-0">
          SaaS modules
        </Typography>
        <Typography variant="caption" className="text-text-secondary m-0 max-w-3xl">
          Search and select one or more modules, then click Add modules. You can remove a row or add
          more at any time.
        </Typography>
      </div>

      <div className="mb-4">
        <Label className="text-text-primary font-medium text-sm mb-1.5 block">Add modules</Label>
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[220px] flex-1 max-w-md flex items-center">
            <Dropdown
              label=""
              name="saas-module-quick-add"
              multiselect
              searchable
              value={modulesPendingAdd}
              onChange={handlePendingSelectionChange}
              options={quickAddOptions}
              placeholder={
                quickAddOptions.length ? "Search and select modules…" : "All catalog modules added"
              }
              clearable={true}
              preserveOptionOrder={true}
              disabled={quickAddOptions.length === 0}
              noWrapper={true}
              className="text-sm w-full h-9"
            />
          </div>
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={modulesPendingAdd.length === 0 || quickAddOptions.length === 0}
            onClick={handleQuickAdd}
            className="shrink-0 h-9"
          >
            Add modules
          </Button>
        </div>
      </div>

      {assignedModuleIds.length === 0 ? (
        <Typography variant="text2" className="text-text-secondary m-0 py-2">
          No modules added yet. Select module(s) above and click &quot;Add modules&quot;.
        </Typography>
      ) : (
        <div className="border border-border-primary rounded-md overflow-x-auto">
          <div className="grid grid-cols-12 gap-1.5 p-3 bg-surface-secondary border-b border-border-primary text-left min-w-[1040px]">
            <div className="col-span-2">
              <Label className="text-text-secondary font-medium text-xs m-0">Module</Label>
            </div>
            <div className="col-span-1">
              <Label className="text-text-secondary font-medium text-xs m-0">Impl.</Label>
            </div>
            <div className="col-span-1">
              <Label className="text-text-secondary font-medium text-xs m-0">Monthly</Label>
            </div>
            <div className="col-span-2">
              <Label className="text-text-secondary font-medium text-xs m-0">Additional Fees</Label>
            </div>
            <div className="col-span-1">
              <Label className="text-text-secondary font-medium text-xs m-0">Billing type</Label>
            </div>
            <div className="col-span-2">
              <Label className="text-text-secondary font-medium text-xs m-0">
                Start date <span className="font-normal opacity-80">(optional)</span>
              </Label>
            </div>
            <div className="col-span-2">
              <Label className="text-text-secondary font-medium text-xs m-0">
                End date <span className="font-normal opacity-80">(optional)</span>
              </Label>
            </div>
            <div className="col-span-1 flex items-center justify-end">
              <Label className="text-text-secondary font-medium text-xs m-0">Active</Label>
            </div>
          </div>
          {assignedModuleIds.map((modId) => {
            const mod = catalogById.get(modId) ?? { id: modId, name: String(modId) };
            const moduleStartDate = watch(`saasModules.${modId}.startDate`);
            const displayName = moduleDisplayLabel(mod);
            return (
              <div
                key={modId}
                className="border-b border-border-primary last:border-b-0 min-w-[1040px]"
              >
                <div className="grid grid-cols-12 gap-1.5 px-3 py-2 items-center bg-surface-primary">
                  <div className="col-span-2 min-w-0 flex items-center gap-1">
                    <Typography
                      variant="text2"
                      className="text-text-primary m-0 font-medium truncate flex-1 min-w-0"
                      title={displayName}
                    >
                      {displayName}
                    </Typography>
                    <button
                      type="button"
                      className="shrink-0 p-1 rounded-sm text-text-secondary hover:text-badge-danger hover:bg-surface-secondary border border-transparent hover:border-border-primary"
                      aria-label={`Remove module ${displayName}`}
                      onClick={() => handleRemove(modId)}
                    >
                      <RiCloseLine className="text-lg" aria-hidden />
                    </button>
                  </div>
                  <div className="col-span-1 min-w-0">
                    <div className="flex items-center border border-border-primary rounded-sm pl-1.5 pr-0.5 py-0 bg-surface-primary">
                      <span className="text-text-secondary text-xs shrink-0">$</span>
                      <Controller
                        name={`saasModules.${modId}.implementationFee`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="text"
                            placeholder="0"
                            noWrapper={true}
                            className="border-0 rounded-none py-1.5 px-1 text-text-primary bg-surface-primary text-xs flex-1 min-w-0 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 shadow-none"
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(toNumericOnly(e.target.value))}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="col-span-1 min-w-0">
                    <div className="flex items-center border border-border-primary rounded-sm pl-1.5 pr-0.5 py-0 bg-surface-primary">
                      <span className="text-text-secondary text-xs shrink-0">$</span>
                      <Controller
                        name={`saasModules.${modId}.monthlyFee`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="text"
                            placeholder="0"
                            noWrapper={true}
                            className="border-0 rounded-none py-1.5 px-1 text-text-primary bg-surface-primary text-xs flex-1 min-w-0 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 shadow-none"
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(toNumericOnly(e.target.value))}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <Controller
                      name={`saasModules.${modId}.additionalFee`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="textarea"
                          rows={2}
                          placeholder="Notes"
                          noWrapper={true}
                          className="border border-border-primary rounded-sm py-1.5 px-2 text-text-primary bg-surface-primary text-xs w-full min-w-0 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 resize-y min-h-[36px]"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-1 min-w-0">
                    <Controller
                      name={`saasModules.${modId}.billingType`}
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label=""
                          name={`saasModules.${modId}.billingType`}
                          value={field.value ?? "both"}
                          onChange={field.onChange}
                          options={SAAS_MODULE_BILLING_TYPE_DROPDOWN_OPTIONS}
                          placeholder="Select billing"
                          searchable={false}
                          clearable={false}
                          preserveOptionOrder={true}
                          noWrapper={true}
                          className="text-xs [&_button]:min-h-[36px] [&_button]:py-1.5"
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-2 min-w-0">
                    <Controller
                      name={`saasModules.${modId}.startDate`}
                      control={control}
                      render={({ field }) => (
                        <SingleDatePicker
                          value={field.value ? { date: field.value } : null}
                          onChange={(val) => field.onChange(val?.date ?? "")}
                          placeholder="Optional"
                          showIcon={true}
                          clearable={true}
                          className="!border-border-primary !rounded-sm !h-9 !py-1.5 text-xs"
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-2 min-w-0">
                    <Controller
                      name={`saasModules.${modId}.endDate`}
                      control={control}
                      render={({ field }) => (
                        <SingleDatePicker
                          value={field.value ? { date: field.value } : null}
                          onChange={(val) => field.onChange(val?.date ?? "")}
                          minDate={moduleStartDate || undefined}
                          placeholder="Optional"
                          showIcon={true}
                          clearable={true}
                          className="!border-border-primary !rounded-sm !h-9 !py-1.5 text-xs"
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-1 flex items-center justify-end pr-1">
                    <Controller
                      name={`saasModules.${modId}.active`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          name={`saasModules.${modId}.active`}
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          noWrapper={true}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
